import React from "react";
import Modal, { ModalSize } from "../../Atoms/Modal";
import { DataSourceItem } from "../../Atoms/Table";
import { ReactComponent as CheckMarkIcon } from "../../../Assets/ActionIcons/CheckMarkIcon.svg";
import { ReactComponent as ExclamationWarningIcon } from "../../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import { ReactComponent as SpinnerIcon } from "../../../Assets/ActionIcons/SpinnerIcon.svg";
import Popover from "../../Atoms/Popover";

export interface BlockActionsModalProps<T> {
  title?: string;
  description?: string;
  isOpen: boolean;
  topOffset?: string | number;
  onClose: () => void;
  data: DataSourceItem<T>[];
  dataValueKey: keyof T;
  width?: string | number;
  size?: ModalSize;
  onAllActionsComplete?: () => void;
  onDataAction: (data: DataSourceItem<T>) => Promise<any>;
}

const BulkActionsModal = <T,>({
  title,
  description,
  isOpen,
  topOffset,
  onClose,
  data,
  dataValueKey,
  width,
  size,
  onDataAction,
  onAllActionsComplete
}: BlockActionsModalProps<T>) => {
  const [statusInfo, setStatsInfo] = React.useState<
    {
      dataKey: string;
      status: "queued" | "loading" | "failed" | "success";
      message?: string;
    }[]
  >([]);

  React.useEffect(() => {
    if (data.length === 0) {
      setStatsInfo([]);
    } else {
      setStatsInfo(
        data.map(d => ({
          dataKey: d.key,
          status: statusInfo.find(s => s.dataKey === d.key)?.status || "queued"
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  React.useEffect(() => {
    const pendingDataHeadOpt = statusInfo.find(s => s.status === "queued");
    const loadingDataHeadOpt = statusInfo.find(s => s.status === "loading");

    if (!loadingDataHeadOpt && pendingDataHeadOpt) {
      const pendingData = data.find(d => d.key === pendingDataHeadOpt.dataKey);
      if (pendingData && isOpen) {
        setStatsInfo(prev =>
          prev.map(s => (s.dataKey === pendingData.key ? { ...s, status: "loading" } : s))
        );
        onDataAction(pendingData)
          .then(() => {
            setStatsInfo(prev =>
              prev.map(s => (s.dataKey === pendingData.key ? { ...s, status: "success" } : s))
            );
          })
          .catch(error => {
            setStatsInfo(prev =>
              prev.map(s =>
                s.dataKey === pendingData.key
                  ? {
                      ...s,
                      status: "failed",
                      message: error
                    }
                  : s
              )
            );
          });
      }
    }

    const allDone = statusInfo.every(s => !(s.status === "queued" || s.status === "loading"));
    if (allDone && statusInfo.length > 0) {
      onAllActionsComplete && onAllActionsComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusInfo, isOpen]);

  const allDone = statusInfo.every(s => !(s.status === "queued" || s.status === "loading"));

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      topOffset={topOffset}
      onClose={onClose}
      width={width}
      size={size}
      footer={null}
    >
      <div className="font-semibold mb-2">{description}</div>
      <div className="max-h-60 overflow-y-auto py-2">
        {data.map(row => {
          const statusRow = statusInfo.find(s => s.dataKey === row.key);
          return (
            <div key={row.key} className="italic flex items-center space-x-2">
              <div>{row.data[dataValueKey]}</div>
              <Popover
                message={
                  statusRow?.status === "loading" ? (
                    <div>Processing</div>
                  ) : statusRow?.status === "failed" ? (
                    <div>{statusRow.message || "Failed"}</div>
                  ) : null
                }
                position="right"
              >
                {statusRow?.status === "success" ? (
                  <CheckMarkIcon className="text-green-600 w-5 h-5" />
                ) : statusRow?.status === "failed" ? (
                  <ExclamationWarningIcon className="text-red-600 w-4 h-4" />
                ) : statusRow?.status === "loading" ? (
                  <SpinnerIcon className="w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" />
                ) : null}
              </Popover>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex space-x-2 items-center">
        {!allDone ? (
          <div className="flex w-full items-center space-x-2">
            <div className="w-full h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-blue-600 rounded-full"
                style={{
                  width: `${
                    (statusInfo.filter(s => s.status === "success" || s.status === "failed")
                      .length /
                      statusInfo.length) *
                    100
                  }%`
                }}
              />
            </div>
            <div className="text-gray-500 mt-2 text-center">
              {statusInfo.filter(s => s.status === "success" || s.status === "failed").length} /{" "}
              {statusInfo.length} completed
            </div>
          </div>
        ) : (
          <>
            <div className="text-gray-500">
              {statusInfo.filter(s => s.status === "success").length} success
            </div>
            <div className="text-gray-500">
              {statusInfo.filter(s => s.status === "failed").length} failed
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default BulkActionsModal;
